import { createPortal } from 'react-dom';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { MICROPHONE_ERROR } from '@/common/functions/audioPlatform';
import {
	Modal,
	ModalCard,
	Info,
	PrimaryButton,
	ActionsRow,
	LinkButton,
	Title,
	SubTitle,
	SectionLabel,
	FlexColumn,
	List,
	ListItem,
	Divider,
	Wrapper,
} from './audioPopup.styles';
import styled from 'styled-components';

const StyledSvg = styled.svg`
	@media (max-width: 600px) {
		width: 12px;
		height: 12px;
	}
`;

const SmallIcon = () => (
	<StyledSvg
		width='16'
		height='16'
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
		aria-hidden
	>
		<path
			d='M12 1v22M1 12h22'
			stroke='#29185e'
			strokeWidth='2'
			strokeLinecap='round'
			opacity='.35'
		/>
	</StyledSvg>
);

const REASON_TO_SUBTITLE_KEY = {
	[MICROPHONE_ERROR.DENIED]: 'deniedSubtitle',
	[MICROPHONE_ERROR.NOT_FOUND]: 'notFoundSubtitle',
	[MICROPHONE_ERROR.IN_USE]: 'inUseSubtitle',
	[MICROPHONE_ERROR.UNSUPPORTED]: 'unsupportedSubtitle',
	[MICROPHONE_ERROR.TIMEOUT]: 'timeoutSubtitle',
	[MICROPHONE_ERROR.UNKNOWN]: 'unknownSubtitle',
	[MICROPHONE_ERROR.MIME_TYPE_UNSUPPORTED]: 'mimeTypeUnsupportedSubtitle',
};

const AudioPopup = ({ setClose, errorReason = MICROPHONE_ERROR.UNKNOWN }) => {
	const translations = useLanguageData('audioError');
	const subtitleKey =
		REASON_TO_SUBTITLE_KEY[errorReason] || REASON_TO_SUBTITLE_KEY[MICROPHONE_ERROR.UNKNOWN];

	const chromeHelpUrl = 'https://support.google.com/chrome/answer/2693767';
	const safariHelpUrl = 'https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac';
	const macHelpUrl = 'https://support.apple.com/guide/mac-help/mchla1b1e1fe/mac';
	const iphoneHelpUrl = 'https://support.apple.com/guide/iphone/iph168c4bbd5/ios';

	return createPortal(
		<Modal onClick={() => setClose((prev) => !prev)}>
			<ModalCard onClick={(e) => e.stopPropagation()}>
				<Wrapper>
					<Title>{translations.title}</Title>
					<SubTitle>{translations[subtitleKey] || translations.subtitle}</SubTitle>
					<SectionLabel>{translations.checkPerms}</SectionLabel>
					<FlexColumn>
						<List>
							<ListItem>
								<SmallIcon />
								<LinkButton
									href={chromeHelpUrl}
									target='_blank'
									rel='noreferrer noopener'
								>
									{translations.guides.chrome}
								</LinkButton>
							</ListItem>
							<ListItem>
								<SmallIcon />
								<LinkButton
									href={safariHelpUrl}
									target='_blank'
									rel='noreferrer noopener'
								>
									{translations.guides.safari}
								</LinkButton>
							</ListItem>
							<ListItem>
								<SmallIcon />
								<LinkButton
									href={macHelpUrl}
									target='_blank'
									rel='noreferrer noopener'
								>
									{translations.guides.mac}
								</LinkButton>
							</ListItem>
							<ListItem>
								<SmallIcon />
								<LinkButton
									href={iphoneHelpUrl}
									target='_blank'
									rel='noreferrer noopener'
								>
									{translations.guides.iphone}
								</LinkButton>
							</ListItem>
						</List>
					</FlexColumn>
					<Divider>{translations.or}</Divider>
					<SectionLabel>{translations.mobileCta}</SectionLabel>
					<ActionsRow>
						<LinkButton
							href='https://play.google.com/store/apps/details?id=com.dreamai.Softai&hl=pl&pli=1'
							target='_blank'
							rel='noreferrer noopener'
							style={{ width: 'fit-content' }}
						>
							{translations.android}
						</LinkButton>
						<LinkButton
							href='https://apps.apple.com/pl/app/Soft-ai/id6743402448'
							target='_blank'
							rel='noreferrer noopener'
							style={{ width: 'fit-content' }}
						>
							{translations.ios}
						</LinkButton>
					</ActionsRow>
					<ActionsRow>
						<PrimaryButton onClick={() => window.location.reload()}>
							{translations.retry}
						</PrimaryButton>
					</ActionsRow>
					<Info>{translations.tipRefresh}</Info>
				</Wrapper>
			</ModalCard>
		</Modal>,
		document.body,
	);
};

export default AudioPopup;
